import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewEncapsulation
} from '@angular/core';
import {ConstantProperty} from '../../_models/constant-property';
import {countLineBreaks, deepCopy} from '../../lib/utils';
import {ColumnFormats} from '../../services/table-utils.service';
import {ClearConstantService} from "./clear-constant.service";
import {takeUntil} from "rxjs/operators";
import {BaseComponent} from "../../shared/base.component";
import {UserService} from '../../services/user.service';
import {TOOLTIP_SHOW_DELAY} from "../../shared/globals";
import {checkApplyNameFormat} from "../../tables/custom-events-table/utils";
import {StringFormatType} from "../../_typing/config/string-format-type";
import {COMMON_FORM_IMPORTS} from "../../shared/import-groups/Common";
import {FloatingInputDirective} from "../../directives/floating-input.directive";
import {FitContentDirective} from "../../directives/fit-content.directive";
import {DblClickCopyDirective} from "../../directives/dbl-click-copy.directive";
import {HoverDelayDirective} from "../../directives/hover-delay.directive";
import {DateTimePickerComponent} from "../../date-time/date-time-picker/date-time-picker.component";
import {FormatNumberPipe, ThousandsSeparatePipe} from "../../shared/pipes";
import {SelectFilterComponent} from "../select-filter/select-filter.component";
import {SelectManyFilterComponent} from "../select-many-filter/select-many-filter.component";
import {AppScope} from '../../services/app_scope.service';
import {ConstantField, ConstantValue, isAggregatedConstant} from '../../_models/api/generic-constant';
import {CommonModule} from "@angular/common";

@Component({
    selector: 'constant-field',
    templateUrl: './constant-field.component.html',
    styleUrls: ['./constant-field.component.less'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        COMMON_FORM_IMPORTS,
        CommonModule,
        FloatingInputDirective,
        FitContentDirective,
        DblClickCopyDirective,
        HoverDelayDirective,
        DateTimePickerComponent,
        ThousandsSeparatePipe,
        FormatNumberPipe,
        SelectFilterComponent,
        SelectManyFilterComponent
    ],
})
export class ConstantFieldComponent extends BaseComponent implements OnChanges {
    private _constant_value: ConstantValue;

    get constant_value(): ConstantValue {
        return this._constant_value;
    }

    @Input() set constant_value(value: ConstantValue) {
        this._constant_value = typeof value === 'string' ? value.trim() : value;
        this.changeDetectorRef.markForCheck();
    }

    @Input() constant_property: ConstantProperty;
    @Input() constant: ConstantField;
    @Input() disabled = false;
    @Input() locked = false;
    @Input() is_focussed = false;
    @Input() auto_class = '';
    @Input() tooltip = '';
    @Input() formats: ColumnFormats;
    @Input() conditional_formats: any;
    @Input() show_sig = true;
    @Input() placeholder = '';
    @Input() allow_fit = false;
    @Input() defaultValue: ConstantValue = null;

    @Output() constant_changed = new EventEmitter<any>();
    @Output() change_complete = new EventEmitter<any>();
    @Output() lock_clicked = new EventEmitter<void>();

    public data_type = 'string';
    public mouseover = false;
    public textarea_rows = 1;
    public elementForConstant = 'input';
    public show_full = false;
    public show_ellipsis = false;
    public can_unlock = false;
    tooltipShowDelay = TOOLTIP_SHOW_DELAY;

    constructor(
        private changeDetectorRef: ChangeDetectorRef,
        private clearConstantService: ClearConstantService,
        private userService: UserService,
        private appScope: AppScope
    ) {
        super();
    }

    ngOnInit(): void {
        this.clearConstantService.clearRequested
            .pipe(takeUntil(this.onDestroy))
            .subscribe(() => {
                this.constant_value = this.resolveDefaultValue();
                this.show_ellipsis = false;
                this.changeDetectorRef.markForCheck();
            });

        this.can_unlock = this.userService.canUnlockData();

        if (!this.constant_value && this.constant_value !== 0) {
            this.constant_value = this.resolveDefaultValue();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        // For aggregated constants, use the data_type from the constant itself,
        // not from the constant_property, because aggregated values have been
        // converted to numeric types even if the original property was user/account/etc
        if (this.constant?.data_type && isAggregatedConstant(this.constant) &&
            this.constant.aggregation && this.constant.aggregation !== 'none') {
            this.data_type = this.constant.data_type;
        } else {
            this.data_type = this.constant_property?.attributes.data_type;
        }

        if (this._constant_value && this.data_type === 'string') {
            this.show_ellipsis = countLineBreaks(this._constant_value) > 1;
        }
        if (this.locked) {
            this.disabled = true;
        }
        this.getElementForConstant();
        this.changeDetectorRef.markForCheck();
    }

    showFull(): void {
        this.mouseover = true;
        if (this.data_type !== 'string') {
            return;
        }
        this.show_full = true;
        this.show_ellipsis = false;
    }

    hideFull(value_changed: boolean): void {
        this.mouseover = false;
        this.show_full = false;
        this.show_ellipsis = countLineBreaks(this._constant_value) > 1;
        this.getElementForConstant();
        if (value_changed) {
            this.changeComplete();
        }
    }

    constantChanged(event: any): void {
        this.constant_changed.emit(this._applyFormat(event));
        this.changeDetectorRef.markForCheck();
    }

    changeComplete(): void {
        this.change_complete.emit(this.constant_value);
        this.changeDetectorRef.markForCheck();
    }

    constantChangedComplete(event: any): void {
        this.constant_value = event;
        this.constantChanged(event);
        this.changeComplete();
    }

    private _applyFormat(value: ConstantValue): ConstantValue {
        if (!checkApplyNameFormat(this.constant_property)) {
            return value;
        }

        switch (this.constant_property.attributes.name_format) {
            case StringFormatType.UPPER:
                return String(value).toUpperCase();
            case StringFormatType.LOWER:
                return String(value).toLowerCase();
            default:
                return value;
        }
    }

    private resolveDefaultValue(): ConstantValue {
        switch (this.defaultValue) {
            case 'currentUser':
                return this.appScope.current_user.id;
            case 'currentAccount':
                return this.appScope.active_account_id;
            default:
                return this.defaultValue;
        }
    }

    getElementForConstant(): void {
        const attr = this.constant_property?.attributes;
        const isDropdown = attr?.is_drop_down_list;
        const isDisabledFloat = this.disabled && this.data_type === 'float';
        const isList = this.constant?.data_type === 'list';

        // Check if this is an aggregated value (has aggregation property and aggregation is not 'none')
        const isAggregated = isAggregatedConstant(this.constant) &&
            this.constant.aggregation &&
            this.constant.aggregation !== 'none';

        if (isList) {
            // For list type with empty/null values, render nothing (blank cell)
            const hasValue = this.constant?.value &&
                Array.isArray(this.constant.value) &&
                this.constant.value.length > 0;
            this.elementForConstant = hasValue ? 'list' : 'none';
        } else if ((isDropdown && !isDisabledFloat) || ['user', 'user_group', 'account'].includes(this.data_type)) {
            this.elementForConstant = 'mat-select';
        } else if (isDisabledFloat) {
            this.elementForConstant = 'div';
        } else if (this.data_type === 'string') {
            this.elementForConstant = this.show_ellipsis ? 'textarea' : 'input';
        } else if (this.data_type === 'float') {
            this.elementForConstant = 'input';
        } else if (this.data_type === 'datetime') {
            this.elementForConstant = 'date-time-picker';
        }
    }

    jsonFilterCompare(a, b) {
        return a == b;
    }

    lockClicked(): void {
        if (this.can_unlock) {
            this.lock_clicked.emit();
        }
    }

    disableItem() {
        return this.disabled;
    }

    stringValue(value: ConstantValue): string {
        return String(value)
    }

    getRelatedComponentTypeOptions(): ConstantValue[] {
        if (this.constant?.value && Array.isArray(this.constant.value)) {
            return deepCopy(this.constant.value);
        }
        return [];
    }
}
